
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import UserDirector from 'Models/Security/Acl/UserDirector';
import VisitorsDirector from 'Models/Security/Acl/VisitorsDirector';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
import moment from 'moment';


export interface IDirectorEntityAttributes extends IModelAttributes {
	firstname: string;
	othername: string;
	surname: string;
	dateOfBirth: Date;
	birthlocation: string;
	birthstate: string;
	companyname: string;
	companyacn: string;
	principaltitle: string;
	appointedDate: Date;
	ceasedDate: Date;
	addresscareof: string;
	addressstreet: string;
	addresssuburb: string;
	addresscity: string;
	addressstate: string;
	addresspostcode: string;
	addresscountry: string;
	din: string;

	boardss: Array<
		| Models.DirectorboardsEntity
		| Models.IDirectorboardsEntityAttributes
	>;
	monitoredId: string;
	monitored: Models.MonitoredEntity | Models.IMonitoredEntityAttributes;

}

@entity('DirectorEntity', 'Director')
export default class DirectorEntity extends Model
	implements IDirectorEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserDirector(),
		new VisitorsDirector(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'FirstName',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public firstname: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'OtherName',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public othername: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'Surname',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public surname: string;
	

	
	@observable
	@attribute<DirectorEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'DateOfBirth',
		displayType: 'datepicker',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public dateOfBirth: Date;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'BirthLocation',
		displayType: 'textfield',
		order: 70,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public birthlocation: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'BirthState',
		displayType: 'textfield',
		order: 80,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public birthstate: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'CompanyName',
		displayType: 'textfield',
		order: 90,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public companyname: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'CompanyAcn',
		displayType: 'textfield',
		order: 100,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public companyacn: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'PrincipalTitle',
		displayType: 'textfield',
		order: 110,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public principaltitle: string;
	

	
	@observable
	@attribute<DirectorEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'AppointedDate',
		displayType: 'datepicker',
		order: 120,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public appointedDate: Date;
	

	
	@observable
	@attribute<DirectorEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	@CRUD({
		name: 'CeasedDate',
		displayType: 'datepicker',
		order: 150,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: attr => AttrUtils.standardiseDate(attr, { serialiseAs: 'date' }),
	})
	public ceasedDate: Date;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'AddressCareOf',
		displayType: 'textfield',
		order: 180,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public addresscareof: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'AddressStreet',
		displayType: 'textfield',
		order: 190,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public addressstreet: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'AddressSuburb',
		displayType: 'textfield',
		order: 200,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public addresssuburb: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'AddressCity',
		displayType: 'textfield',
		order: 210,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public addresscity: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'AddressState',
		displayType: 'textfield',
		order: 220,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public addressstate: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'AddressPostcode',
		displayType: 'textfield',
		order: 230,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public addresspostcode: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'AddressCountry',
		displayType: 'textfield',
		order: 240,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public addresscountry: string;

	@observable
	@attribute<DirectorEntity, string>()
	@CRUD({
		name: 'DIN',
		displayType: 'textfield',
		order: 250,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public din: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Boardss',
		displayType: 'reference-multicombobox',
		order: 260,
		referenceTypeFunc: () => Models.DirectorboardsEntity,
		disableDefaultOptionRemoval: true,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'boardss',
			oppositeEntity: () => Models.DirectorboardsEntity,
		}),
		
	})
	public boardss: Models.DirectorboardsEntity[] = [];

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'Monitored',
		displayType: 'reference-combobox',
		order: 270,
		referenceTypeFunc: () => Models.MonitoredEntity,
		
	})
	public monitoredId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public monitored: Models.MonitoredEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IDirectorEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IDirectorEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.firstname !== undefined) {
				this.firstname = attributes.firstname;
			}
			if (attributes.othername !== undefined) {
				this.othername = attributes.othername;
			}
			if (attributes.surname !== undefined) {
				this.surname = attributes.surname;
			}
			if (attributes.dateOfBirth !== undefined) {
				if (attributes.dateOfBirth === null) {
					this.dateOfBirth = attributes.dateOfBirth;
				} else {
					this.dateOfBirth = moment(attributes.dateOfBirth).toDate();
				}
			}
			if (attributes.birthlocation !== undefined) {
				this.birthlocation = attributes.birthlocation;
			}
			if (attributes.birthstate !== undefined) {
				this.birthstate = attributes.birthstate;
			}
			if (attributes.companyname !== undefined) {
				this.companyname = attributes.companyname;
			}
			if (attributes.companyacn !== undefined) {
				this.companyacn = attributes.companyacn;
			}
			if (attributes.principaltitle !== undefined) {
				this.principaltitle = attributes.principaltitle;
			}
			if (attributes.appointedDate !== undefined) {
				if (attributes.appointedDate === null) {
					this.appointedDate = attributes.appointedDate;
				} else {
					this.appointedDate = moment(attributes.appointedDate).toDate();
				}
			}
			if (attributes.ceasedDate !== undefined) {
				if (attributes.ceasedDate === null) {
					this.ceasedDate = attributes.ceasedDate;
				} else {
					this.ceasedDate = moment(attributes.ceasedDate).toDate();
				}
			}
			if (attributes.addresscareof !== undefined) {
				this.addresscareof = attributes.addresscareof;
			}
			if (attributes.addressstreet !== undefined) {
				this.addressstreet = attributes.addressstreet;
			}
			if (attributes.addresssuburb !== undefined) {
				this.addresssuburb = attributes.addresssuburb;
			}
			if (attributes.addresscity !== undefined) {
				this.addresscity = attributes.addresscity;
			}
			if (attributes.addressstate !== undefined) {
				this.addressstate = attributes.addressstate;
			}
			if (attributes.addresspostcode !== undefined) {
				this.addresspostcode = attributes.addresspostcode;
			}
			if (attributes.addresscountry !== undefined) {
				this.addresscountry = attributes.addresscountry;
			}
			if (attributes.din !== undefined) {
				this.din = attributes.din;
			}
			if (attributes.boardss !== undefined && Array.isArray(attributes.boardss)) {
				for (const model of attributes.boardss) {
					if (model instanceof Models.DirectorboardsEntity) {
						this.boardss.push(model);
					} else {
						this.boardss.push(new Models.DirectorboardsEntity(model));
					}
				}
			}
			if (attributes.monitoredId !== undefined) {
				this.monitoredId = attributes.monitoredId;
			}
			if (attributes.monitored !== undefined) {
				if (attributes.monitored === null) {
					this.monitored = attributes.monitored;
				} else if (attributes.monitored instanceof Models.MonitoredEntity) {
					this.monitored = attributes.monitored;
					this.monitoredId = attributes.monitored.id;
				} else {
					this.monitored = new Models.MonitoredEntity(attributes.monitored);
					this.monitoredId = this.monitored.id;
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		boardss {
			${Models.DirectorboardsEntity.getAttributes().join('\n')}
		}
		monitored {
			${Models.MonitoredEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			boardss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(DirectorEntity.prototype, 'created');
CRUD(modifiedAttr)(DirectorEntity.prototype, 'modified');
